import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Target, LogOut, Users, Crown, Share2, CreditCard, Check, RotateCcw, Settings, SlidersHorizontal } from 'lucide-react';
import { useAuthContext } from '../../../providers';
import { theme } from '../../../styles/theme';
import {
  ProfileTopNavContainer,
  ProfileTopNavLeft,
  ProfileTopNavRight,
  ProfileTopNavButton,
  ProfileTopNavIcon,
  ProfileTopNavTitle,
  PremiumBadge,
  HamburgerIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuIcon,
  DropdownMenuText,
  DropdownMenuDivider,
  DropdownBackdrop,
  MenuWrapper,
  NotificationToast
} from '../../../styles/Profile/ProfileTopNav.styles';
import { logger } from '../../../utils/logger';
import { Icon } from '../../../components/shared';
import { useSubscriptionContext } from '../../../providers/SubscriptionProvider';

interface ProfileTopNavProps {
  title?: string;
  isOwnProfile?: boolean;
  onShare?: () => void;
  onFindFriends?: () => void;
  onPremium?: () => void;
  onCancelSubscription?: () => void;
  /** Opens the in-app subscription manager: plan, renewal date, up/downgrade. */
  onManageSubscription?: () => void;
  /** Undo a pending cancellation while the paid period is still running. */
  onReactivateSubscription?: () => void;
  onEdit?: () => void;
}

export const ProfileTopNav: React.FC<ProfileTopNavProps> = ({
  title = 'Profile',
  isOwnProfile = false,
  onShare,
  onFindFriends,
  onPremium,
  onCancelSubscription,
  onManageSubscription,
  onReactivateSubscription,
  onEdit
}) => {
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Which billing action to offer. The menu previously showed "Cancel
  // Subscription" to everyone, including people who had never subscribed.
  //
  // Read from the shared subscription state rather than fetched here. This
  // component used to call /billing/subscription itself, so the menu and the
  // feature gates resolved the plan independently and could disagree — the
  // menu offering "Go Premium" to someone the gates already treated as
  // Premium was exactly that bug.
  const { subscription, refresh: refreshSubscription } = useSubscriptionContext();

  // Still re-read when the menu opens: it is opened rarely, and it is where
  // the billing actions live, so it should reflect the state now rather than
  // whatever was true when the page loaded.
  useEffect(() => {
    if (!isMenuOpen || !isOwnProfile) return;
    refreshSubscription();
  }, [isMenuOpen, isOwnProfile, refreshSubscription]);
  const [showShareToast, setShowShareToast] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Lock body scroll when menu is open on mobile
  useEffect(() => {
    if (isMenuOpen && window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      // Default share functionality
      const shareData = {
        title: 'Check out this profile!',
        text: 'Take a look at this amazing profile on Plenty of Hearts',
        url: window.location.href
      };

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        navigator.share(shareData).catch(err => {
          // If share fails or is cancelled, fallback to clipboard if acceptable, 
          // or just log. Usually we fallback only if share NOT available.
          console.log('Share API error or cancelled', err);
        });
      } else {
        navigator.clipboard.writeText(window.location.href).then(() => {
          setShowShareToast(true);
          setTimeout(() => setShowShareToast(false), 3000);
        }).catch(err => {
          console.error('Failed to copy', err);
          alert('Failed to copy link');
        });
      }
    }
  };

  const handleFindFriends = () => {
    if (onFindFriends) {
      onFindFriends();
    } else {
      navigate('/community-chat-rooms');
    }
  };

  const handlePremium = () => {
    if (onPremium) {
      onPremium();
    } else {
      navigate('/premium');
    }
  };


  const handleLogout = async () => {
    setIsMenuOpen(false);
    logger.log('Starting logout process...');

    try {
      logger.log('Calling logout function...');
      await logout();
      logger.log('Logout completed, navigating to login page...');

      // Use window.location.href to force a full page refresh
      // This ensures all state is cleared and the user is properly logged out
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      // Force navigation even if logout fails
      window.location.href = '/login';
    }
  };

  const handleOnboarding = () => {
    setIsMenuOpen(false);
    navigate('/onboarding', { state: { reset: true } });
  };

  const handleMenuItem = (action: string) => {
    setIsMenuOpen(false);

    switch (action) {
      case 'edit':
        if (onEdit) {
          onEdit();
        }
        break;
      case 'onboarding':
        handleOnboarding();
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'manage-subscription':
        onManageSubscription?.();
        break;
      case 'cancel-subscription':
        // No window.confirm fallback: the host page owns this, and a bare
        // confirm() that then did nothing was worse than no menu item.
        onCancelSubscription?.();
        break;
      case 'reactivate-subscription':
        onReactivateSubscription?.();
        break;
      case 'subscribe':
        onPremium?.();
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <DropdownBackdrop $isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />

      <ProfileTopNavContainer $menuOpen={isMenuOpen}>
        <ProfileTopNavLeft>
          {/* Hamburger Menu */}
          <MenuWrapper ref={menuRef}>
            <ProfileTopNavButton onClick={handleMenuToggle} title="Menu">
              <HamburgerIcon $isOpen={isMenuOpen}>
                <span />
                <span />
                <span />
              </HamburgerIcon>
            </ProfileTopNavButton>

            {/* Dropdown Menu */}
            <DropdownMenu $isOpen={isMenuOpen}>
              {isOwnProfile && (
                <>
                  <DropdownMenuItem onClick={() => handleMenuItem('edit')}>
                    <DropdownMenuIcon>
                      <Icon icon={Edit} size={18} />
                    </DropdownMenuIcon>
                    <DropdownMenuText>Edit Profile</DropdownMenuText>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleMenuItem('settings')}>
                    <DropdownMenuIcon>
                      <Icon icon={SlidersHorizontal} size={18} />
                    </DropdownMenuIcon>
                    <DropdownMenuText>Settings</DropdownMenuText>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleMenuItem('onboarding')}>
                    <DropdownMenuIcon>
                      <Icon icon={Target} size={18} />
                    </DropdownMenuIcon>
                    <DropdownMenuText>Complete Onboarding</DropdownMenuText>
                  </DropdownMenuItem>
                  {/* One billing action, chosen from the live subscription
                      state. Cancelling and having ended are different things:
                      a pending cancellation can be undone in place, while a
                      finished subscription needs a fresh checkout. */}
                  {subscription && (
                    <>
                      <DropdownMenuDivider />
                      {/* Manage Subscription sits above Cancel deliberately:
                          seeing which plan you are on, and changing it, should
                          come before the option to end it. Shown to anyone with
                          a subscription; someone who never subscribed gets
                          'Go Premium' below instead. */}
                      {subscription.hasSubscription && (
                        <DropdownMenuItem onClick={() => handleMenuItem('manage-subscription')}>
                          <DropdownMenuIcon>
                            <Icon icon={Settings} size={18} />
                          </DropdownMenuIcon>
                          <DropdownMenuText>Manage Subscription</DropdownMenuText>
                        </DropdownMenuItem>
                      )}
                      {subscription.canCancel && (
                        <DropdownMenuItem onClick={() => handleMenuItem('cancel-subscription')}>
                          <DropdownMenuIcon>
                            <Icon icon={CreditCard} size={18} />
                          </DropdownMenuIcon>
                          <DropdownMenuText>Cancel Subscription</DropdownMenuText>
                        </DropdownMenuItem>
                      )}
                      {subscription.canResume && (
                        <DropdownMenuItem onClick={() => handleMenuItem('reactivate-subscription')}>
                          <DropdownMenuIcon>
                            <Icon icon={RotateCcw} size={18} />
                          </DropdownMenuIcon>
                          <DropdownMenuText>Reactivate Subscription</DropdownMenuText>
                        </DropdownMenuItem>
                      )}
                      {!subscription.canCancel && !subscription.canResume && (
                        <DropdownMenuItem onClick={() => handleMenuItem('subscribe')}>
                          <DropdownMenuIcon>
                            <Icon icon={Crown} size={18} />
                          </DropdownMenuIcon>
                          <DropdownMenuText>
                            {subscription.hasSubscription ? 'Resubscribe' : 'Go Premium'}
                          </DropdownMenuText>
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  <DropdownMenuDivider />
                  <DropdownMenuItem onClick={() => handleMenuItem('logout')}>
                    <DropdownMenuIcon>
                      <Icon icon={LogOut} size={18} />
                    </DropdownMenuIcon>
                    <DropdownMenuText>Logout</DropdownMenuText>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenu>
          </MenuWrapper>
        </ProfileTopNavLeft>

        {/* Title */}
        <ProfileTopNavTitle>{title}</ProfileTopNavTitle>

        <ProfileTopNavRight>
          {/* Find Friends Icon */}
          <ProfileTopNavButton onClick={handleFindFriends} title="Find Friends">
            <ProfileTopNavIcon>
              <Icon icon={Users} size={24} />
            </ProfileTopNavIcon>
          </ProfileTopNavButton>

          {/* Premium Membership Icon */}
          <ProfileTopNavButton $isPremium onClick={handlePremium} title="Premium">
            <ProfileTopNavIcon $isPremium $size="1.8rem">
              <Icon icon={Crown} size={28} />
            </ProfileTopNavIcon>
          </ProfileTopNavButton>

          {/* Share Icon */}
          <ProfileTopNavButton onClick={handleShare} title="Share">
            <ProfileTopNavIcon>
              <Icon icon={Share2} size={24} />
            </ProfileTopNavIcon>
          </ProfileTopNavButton>
        </ProfileTopNavRight>
      </ProfileTopNavContainer >

      <NotificationToast $isVisible={showShareToast}>
        <Icon icon={Check} size={16} color={theme.colors.success} />
        Profile link copied to clipboard
      </NotificationToast>

    </>
  );
};

// Set display name for identification in DevTools
ProfileTopNav.displayName = 'ProfileTopNav';

